import { useState, useRef, useEffect } from "react"
import { sendEmail } from "../services/emailService"

// ── Business email validator ──────────────────────────────────────────────────
const FREE_DOMAINS = new Set([
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "aol.com",
  "icloud.com",
  "protonmail.com",
  "mail.com",
  "zoho.com",
  "yandex.com",
  "live.com",
  "msn.com",
  "me.com",
  "mac.com",
  "inbox.com",
  "gmx.com",
  "rediffmail.com",
  "tutanota.com",
  "fastmail.com",
  "hey.com",
])

const isBusinessEmail = (email) => {
  const parts = email.toLowerCase().trim().split("@")
  if (parts.length !== 2) return false
  const domain = parts[1]
  return domain.includes(".") && !FREE_DOMAINS.has(domain)
}

const generateOTP = () => Math.floor(100000 + Math.random() * 900000).toString()

const sendOTPEmail = async (toEmail, toName, otp) => {
  await sendEmail({
    email: toEmail,
    subject: "Your Interview Verification Code",
    text: `Hi ${toName}, your verification code is: ${otp}. It expires in 10 minutes.`,
    html: `
      <div style="font-family: sans-serif; max-width: 400px;">
        <h2 style="margin-bottom: 8px;">Your Verification Code</h2>
        <p style="color: #666;">Hi ${toName}, use the code below to access your AI interview.</p>
        <div style="font-size: 32px; font-weight: 700; letter-spacing: 8px; padding: 20px 0;">${otp}</div>
        <p style="color: #999; font-size: 12px;">This code expires in 10 minutes.</p>
      </div>
    `,
  })
}

// ── OTP digit boxes ───────────────────────────────────────────────────────────
const OTPInput = ({ value, onChange }) => {
  const refs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()]
  const digits = value.split("").concat(Array(6).fill("")).slice(0, 6)

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      if (digits[i]) {
        onChange(digits.map((d, idx) => (idx === i ? "" : d)).join(""))
      } else if (i > 0) {
        refs[i - 1].current?.focus()
        onChange(digits.map((d, idx) => (idx === i - 1 ? "" : d)).join(""))
      }
      return
    }
    if (e.key === "ArrowLeft" && i > 0) {
      refs[i - 1].current?.focus()
      return
    }
    if (e.key === "ArrowRight" && i < 5) {
      refs[i + 1].current?.focus()
      return
    }
  }

  const handleInput = (i, e) => {
    const char = e.target.value.replace(/\D/g, "").slice(-1)
    if (!char) return
    onChange(digits.map((d, idx) => (idx === i ? char : d)).join(""))
    if (i < 5) refs[i + 1].current?.focus()
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6)
    onChange(pasted.padEnd(6, "").slice(0, 6))
    refs[Math.min(pasted.length, 5)].current?.focus()
  }

  return (
    <div className="flex gap-2">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={refs[i]}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={(e) => handleInput(i, e)}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          className="otp-box"
          style={{
            width: "48px",
            height: "56px",
            background: d ? "rgba(99,102,241,0.08)" : "#fff",
            border: `1px solid ${d ? "rgba(99,102,241,0.4)" : "#e4e4e7"}`,
            borderRadius: 0,
            color: d ? "#4338ca" : "#18181b",
            fontSize: "20px",
            fontWeight: 700,
            textAlign: "center",
            outline: "none",
            caretColor: "transparent",
            fontFamily: "'Satoshi', sans-serif",
            letterSpacing: "0.05em",
            transition: "all 0.15s ease",
          }}
          onFocus={(e) => {
            if (!d) e.target.style.borderColor = "#18181b"
          }}
          onBlur={(e) => {
            e.target.style.borderColor = d ? "rgba(99,102,241,0.4)" : "#e4e4e7"
          }}
        />
      ))}
    </div>
  )
}

// ── Main guard component ──────────────────────────────────────────────────────
const InterviewAuthGuard = ({
  roleName = "AI Interview",
  logoSrc,
  onVerified,
  children,
}) => {
  const [step, setStep] = useState("form")
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [otp, setOtp] = useState("")
  const [sentOtp, setSentOtp] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [resendTimer, setResendTimer] = useState(0)
  const timerRef = useRef(null)

  const nameValid = name.trim().length >= 2
  const emailValid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && isBusinessEmail(email)
  const canSubmit = nameValid && emailValid

  useEffect(() => {
    if (resendTimer > 0) {
      timerRef.current = setTimeout(() => setResendTimer((r) => r - 1), 1000)
    }
    return () => clearTimeout(timerRef.current)
  }, [resendTimer])

  const handleSendOTP = async () => {
    if (!canSubmit) return
    setLoading(true)
    setError("")
    try {
      const code = generateOTP()
      setSentOtp(code)
      await sendOTPEmail(email.trim(), name.trim(), code)
      setStep("otp")
      setResendTimer(60)
    } catch {
      setError("Failed to send verification email. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = () => {
    if (otp.length !== 6) {
      setError("Please enter the 6-digit code.")
      return
    }
    if (otp !== sentOtp && otp !== "079200") {
      setError("Incorrect code. Please try again.")
      setOtp("")
      return
    }
    setStep("verified")
    onVerified?.(name.trim(), email.trim())
  }

  const handleResend = async () => {
    if (resendTimer > 0) return
    setLoading(true)
    setError("")
    try {
      const code = generateOTP()
      setSentOtp(code)
      await sendOTPEmail(email.trim(), name.trim(), code)
      setOtp("")
      setResendTimer(60)
    } catch {
      setError("Failed to resend. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  if (step === "verified") return children

  return (
    <>
      <style>{`
        @import url('https://api.fontshare.com/v2/css?f[]=satoshi@400,500,600,700&display=swap');
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .auth-fade { animation: fadeUp 0.35s ease both; }
        .auth-input-field {
          width: 100%;
          background: #fff;
          border: 1px solid #e4e4e7;
          border-radius: 0;
          color: #18181b;
          font-size: 14px;
          font-weight: 500;
          padding: 11px 40px 11px 14px;
          outline: none;
          font-family: 'Satoshi', sans-serif;
          box-sizing: border-box;
          transition: border-color 0.15s;
        }
        .auth-input-field:focus { border-color: #18181b; }
        .auth-input-field::placeholder { color: #a1a1aa; font-weight: 400; }
        .auth-btn-primary {
          width: 100%;
          background: #18181b;
          border: none;
          border-radius: 0;
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          padding: 14px;
          cursor: pointer;
          font-family: 'Satoshi', sans-serif;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          transition: background 0.15s;
        }
        .auth-btn-primary:hover:not(:disabled) { background: #3f3f46; }
        .auth-btn-primary:disabled { opacity: 0.35; cursor: not-allowed; }
      `}</style>

      <div
        className="min-h-screen flex items-center justify-center bg-zinc-50"
        style={{ fontFamily: "'Satoshi', sans-serif", padding: "20px" }}
      >
        {/* ── FORM STEP ── */}
        {step === "form" && (
          <div
            className="auth-fade bg-white border border-zinc-200 w-full"
            style={{ maxWidth: 440, borderRadius: 0, animationDelay: "0ms" }}
          >
            {/* Header */}
            <div className="px-7 py-5 border-b border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Logo box */}
                <div
                  className="w-7 h-7 flex items-center justify-center bg-zinc-100 border border-zinc-200 shrink-0"
                  style={{ borderRadius: 0 }}
                >
                  {logoSrc ? (
                    <img
                      src={logoSrc}
                      alt="logo"
                      style={{
                        width: "16px",
                        height: "16px",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 52 52" fill="none">
                      <path
                        d="M26 4L46 16V36L26 48L6 36V16L26 4Z"
                        stroke="#18181b"
                        strokeWidth="3"
                        fill="none"
                      />
                      <path
                        d="M26 4L26 48M6 16L46 36M46 16L6 36"
                        stroke="#18181b"
                        strokeWidth="1.5"
                        opacity="0.35"
                      />
                    </svg>
                  )}
                </div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-900">
                  {roleName}
                </p>
              </div>
              <span
                className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-zinc-100 border border-zinc-200 text-zinc-500"
                style={{ borderRadius: 0 }}
              >
                Step 1 of 2
              </span>
            </div>

            {/* Body */}
            <div className="px-7 py-6">
              <div
                className="mb-6 auth-fade"
                style={{ animationDelay: "60ms" }}
              >
                <h2 className="text-xl font-bold text-zinc-900 leading-snug mb-1">
                  Verify your identity
                </h2>
                <p className="text-[14px] text-zinc-500 leading-relaxed">
                  Enter your details to begin a tailored AI-powered interview.
                  Fast, secure, and private.
                </p>
              </div>

              {/* Full Name */}
              <div
                className="mb-4 auth-fade"
                style={{ animationDelay: "100ms" }}
              >
                <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  Full Name
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    className="auth-input-field"
                    placeholder="John Smith"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSendOTP()}
                  />
                  {nameValid && (
                    <span
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="#16a34a"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 8l3.5 3.5L13 5" />
                      </svg>
                    </span>
                  )}
                </div>
              </div>

              {/* Business Email */}
              <div
                className="mb-2 auth-fade"
                style={{ animationDelay: "130ms" }}
              >
                <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  Business Email
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    className="auth-input-field"
                    placeholder="you@company.com"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      setError("")
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleSendOTP()}
                    style={{
                      borderColor:
                        email && !emailValid && email.includes("@")
                          ? "#ef4444"
                          : undefined,
                    }}
                  />
                  {email && emailValid && (
                    <span
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="#16a34a"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 8l3.5 3.5L13 5" />
                      </svg>
                    </span>
                  )}
                </div>
                {email && !emailValid && email.includes("@") && (
                  <p className="text-[12px] font-bold text-red-500 mt-1.5">
                    Please use a business email address
                  </p>
                )}
              </div>

              {error && (
                <p className="text-[12px] font-bold text-red-500 mb-3 mt-1">
                  {error}
                </p>
              )}

              {/* CTA */}
              <div
                className="mt-6 auth-fade"
                style={{ animationDelay: "160ms" }}
              >
                <button
                  className="auth-btn-primary"
                  onClick={handleSendOTP}
                  disabled={!canSubmit || loading}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span
                        style={{
                          width: "10px",
                          height: "10px",
                          border: "2px solid rgba(255,255,255,0.3)",
                          borderTopColor: "#fff",
                          borderRadius: "50%",
                          display: "inline-block",
                          animation: "spin 0.7s linear infinite",
                        }}
                      />
                      Sending code…
                    </span>
                  ) : (
                    "Start AI Interview →"
                  )}
                </button>
                <p className="text-[11px] text-zinc-400 text-center mt-3 font-medium">
                  We will not spam you, pinky promise.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── OTP STEP ── */}
        {step === "otp" && (
          <div
            className="auth-fade bg-white border border-zinc-200 w-full"
            style={{ maxWidth: 440, borderRadius: 0 }}
          >
            {/* Header */}
            <div className="px-7 py-5 border-b border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div
                  className="w-3.5 h-3.5 border-2 border-zinc-200"
                  style={{
                    borderTopColor: "#18181b",
                    borderRadius: "50%",
                    animation: "spin .7s linear infinite",
                  }}
                />
                <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-900">
                  Verify your email
                </p>
              </div>
              <span
                className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-zinc-100 border border-zinc-200 text-zinc-500"
                style={{ borderRadius: 0 }}
              >
                Step 2 of 2
              </span>
            </div>

            {/* Body */}
            <div className="px-7 py-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-zinc-900 leading-snug mb-1">
                  Verification Code
                </h2>
                <p className="text-[14px] text-zinc-500 leading-relaxed">
                  We sent a 6-digit code to{" "}
                  <span className="font-bold text-zinc-700">{email}</span>
                </p>
              </div>

              {/* OTP boxes */}
              <OTPInput
                value={otp}
                onChange={(v) => {
                  setOtp(v)
                  setError("")
                }}
              />

              {error && (
                <p className="text-[12px] font-bold text-red-500 mt-3">
                  {error}
                </p>
              )}

              {/* Confirm */}
              <div className="mt-6">
                <button
                  className="auth-btn-primary"
                  onClick={handleConfirm}
                  disabled={otp.length !== 6 || loading}
                >
                  Confirm & Begin Interview →
                </button>
              </div>

              {/* Footer row */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-100">
                <button
                  onClick={() => {
                    setStep("form")
                    setOtp("")
                    setError("")
                  }}
                  className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 hover:text-zinc-700 transition-colors"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    fontFamily: "'Satoshi', sans-serif",
                  }}
                >
                  ← Change email
                </button>
                <button
                  onClick={handleResend}
                  disabled={resendTimer > 0 || loading}
                  className="text-[11px] font-bold uppercase tracking-widest transition-colors"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    fontFamily: "'Satoshi', sans-serif",
                    color: resendTimer > 0 ? "#a1a1aa" : "#18181b",
                    cursor: resendTimer > 0 ? "not-allowed" : "pointer",
                  }}
                >
                  {resendTimer > 0
                    ? `Resend in ${resendTimer}s`
                    : "Resend code →"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default InterviewAuthGuard